import type {
  ClaimAddRequest,
  ClaimOrderRequest,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import type { Lanes, Landing } from "@/components/ui/SortableLanes";

/** The lanes' names: every independent claim in one list, and the claims directly beneath a claim. */
export const INDEPENDENTS = "independents";
export const underClaim = (claimId: string) => `under:${claimId}`;
/** What a position is dragged as, and the two things it can land on: a claim, or the set itself. */
export const positionItem = (positionId: string) => `position:${positionId}`;
export const ontoClaim = (claimId: string) => `onto:${claimId}`;
export const INTO_SET = "into:set";

/** The set as lanes, in the order it is served. */
export function claimLanes(stage: ClaimStrategyResponse): Lanes {
  const lanes: Lanes = { [INDEPENDENTS]: [] };
  for (const claim of stage.claims) {
    if (claim.kind === "independent") lanes[INDEPENDENTS].push(claim.claim_id);
    lanes[underClaim(claim.claim_id)] ??= [];
  }
  for (const claim of stage.claims) {
    if (claim.parent_claim_id !== null) {
      (lanes[underClaim(claim.parent_claim_id)] ??= []).push(claim.claim_id);
    }
  }
  return lanes;
}

/** The writes a finished drag asks for: one per list it rearranged, naming that list's siblings. */
export function orderWrites(
  served: Lanes,
  arranged: Lanes,
): ClaimOrderRequest[] {
  const changed = (lane: string) =>
    JSON.stringify(served[lane]) !== JSON.stringify(arranged[lane]);
  const writes: ClaimOrderRequest[] = [];
  for (const lane of Object.keys(arranged)) {
    if (!changed(lane)) continue;
    const ids = arranged[lane];
    if (lane === INDEPENDENTS) writes.push({ claim_ids: ids });
    else if (lane.startsWith("under:")) {
      writes.push({ claim_ids: ids, parent_claim_id: lane.slice(6) });
    }
  }
  return writes;
}

/** Where a dropped position goes: under the claim it landed on, or new in the set, tagged from its words. */
export function placementOf(
  landing: Landing,
  stage: ClaimStrategyResponse,
): ClaimAddRequest | null {
  if (!landing.source.startsWith("position:")) return null;
  const positionId = landing.source.slice("position:".length);
  if (landing.target.startsWith("onto:")) {
    const claim = stage.claims.find(
      (c) => c.claim_id === landing.target.slice("onto:".length),
    );
    return claim === undefined
      ? null
      : { parent_claim_id: claim.claim_id, position_id: positionId };
  }
  if (landing.target === INTO_SET) return { position_id: positionId };
  return null;
}

/** What the attorney is told a placement made, read off the claim the answer holds that the set did not. */
export function placedWords(
  before: ClaimStrategyResponse,
  after: ClaimStrategyResponse,
  request: ClaimAddRequest,
): string {
  const held = new Set(before.claims.map((c) => c.claim_id));
  const made = after.claims.find((c) => !held.has(c.claim_id));
  if (made === undefined) return "Placed in the claim set.";
  const number = String(made.number);
  if (request.parent_claim_id != null) {
    const parent = after.claims.find(
      (c) => c.claim_id === request.parent_claim_id,
    );
    return `Placed as dependent claim ${number} under claim ${String(parent?.number ?? "")}.`;
  }
  if (after.tag_queued) {
    return `Placed as new independent claim ${number}. Patent Studio tags the category from the wording.`;
  }
  const category = after.forms.find((f) => f.form_id === made.form_id);
  return `Placed as new independent claim ${number}, tagged ${category?.name ?? "its category"}.`;
}

/** What a position shows while it is carried: its title, as its row reads. */
export function carriedWords(
  stage: ClaimStrategyResponse,
  id: string,
): string | null {
  if (!id.startsWith("position:")) return null;
  const positionId = id.slice("position:".length);
  const position = stage.unplaced.find((p) => p.position_id === positionId);
  return position === undefined ? null : position.title;
}

/** Marks the claim a placement made as being tagged, when the write asked Praxis to tag it. */
export function markPlacedTagging(
  before: ClaimStrategyResponse,
  after: ClaimStrategyResponse,
  markTagging: (claimId: string, formId: string) => void,
): void {
  if (!after.tag_queued) return;
  const held = new Set(before.claims.map((c) => c.claim_id));
  const made = after.claims.find((c) => !held.has(c.claim_id));
  if (made !== undefined) markTagging(made.claim_id, made.form_id);
}
